import React, { useMemo, useState } from 'react';
import { Button, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { BsArrowDown, BsArrowUp, BsArrowsFullscreen, BsGrid3X3Gap, BsImage, BsPalette, BsPlus, BsSlashCircle, BsTextCenter, BsTextLeft, BsTextRight, BsTrash, BsYoutube } from 'react-icons/bs';
import { v4 as uuid } from 'uuid';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import CardPicker from './card-picker/SetCardPicker';
import ManagedImageField from './fields/ManagedImageField';
import BackgroundField from './fields/BackgroundField';
import { presetOptionsForField } from './config/presentationPresets';
import { cleanHtml } from './utils/presentation';

const CARD_TYPES = ['cards', 'deckcards', 'heropower'];
const CARD_LIMITS = { cards: 60, deckcards: 40, heropower: 1 };
const MAX_CARD_LIMIT = 300;
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link'];

const VISUAL_SELECTORS = new Set([
  'contentAlign', 'imagePosition', 'portraitPosition', 'mediaPosition',
  'backgroundPosition', 'backgroundSize', 'imageFit', 'columns', 'mobileColumns', 'theme',
  'gradientDirection',
]);

const choiceIcon = (value) => {
  if (value === 'left') return <BsTextLeft />;
  if (value === 'center') return <BsTextCenter />;
  if (value === 'right') return <BsTextRight />;
  if (value === 'center top' || value === 'top') return <BsArrowUp />;
  if (value === 'center bottom' || value === 'bottom') return <BsArrowDown />;
  if (value === 'cover') return <BsArrowsFullscreen />;
  if (value === 'contain') return <BsGrid3X3Gap />;
  if (value === 'image') return <BsImage />;
  if (value === 'video') return <BsYoutube />;
  if (value === 'color') return <BsPalette />;
  if (value === 'none') return <BsSlashCircle />;
  return <span className="set-choice-symbol">{String(value).replace('center ', '').slice(0, 3)}</span>;
};

function VisualSelector({ options, value, onChange, label }) {
  return <div className="set-visual-selector" role="radiogroup" aria-label={label}>{options.map((option) => <button key={option.key} type="button" role="radio" aria-checked={value === option.key} className={value === option.key ? 'set-is-selected' : ''} onClick={() => onChange(option.key)}>{choiceIcon(option.key)}<span>{option.label}</span></button>)}</div>;
}

function CardField({ type, value, onChange, maxCards }) {
  const [open, setOpen] = useState(false);
  const cards = Array.isArray(value) ? value : [];
  const deckMode = type === 'deckcards';
  const configuredLimit = Number(maxCards);
  const limit = Number.isFinite(configuredLimit) && configuredLimit > 0
    ? Math.min(MAX_CARD_LIMIT, Math.floor(configuredLimit))
    : CARD_LIMITS[type];
  const totalCards = deckMode
    ? cards.reduce((total, card) => total + Math.max(1, Number(card?.quantity) || 1), 0)
    : cards.length;
  return (
    <div>
      <div className="set-card-field-summary">
        <span><strong>{totalCards}</strong> {deckMode ? `deck cards (${cards.length} unique)` : 'cards'} selected</span>
        <Button size="sm" variant="outline-primary" onClick={() => setOpen(true)}>{cards.length ? 'Edit selection' : 'Select cards'}</Button>
      </div>
      {open && (
        <CardPicker
          type={deckMode ? 'deckcard' : 'attach'} card={null}
          onConfirm={(selected) => { onChange(selected); setOpen(false); }}
          onClose={() => setOpen(false)} limit={limit}
          deckCardMode={deckMode} preSelectedCards={cards} pickerMode={type}
        />
      )}
    </div>
  );
}

function SocialFields({ options, value, onChange }) {
  const socials = Array.isArray(value) ? value : [];
  const [platform, setPlatform] = useState('');
  const [handle, setHandle] = useState('');
  const available = options.filter((option) => !socials.some((social) => social.platform === option.key));
  const update = (id, patch) => onChange(socials.map((social) => social.id === id ? { ...social, ...patch } : social));
  const move = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= socials.length) return;
    const next = [...socials];
    [next[index], next[nextIndex]] = [next[nextIndex], next[index]];
    onChange(next);
  };
  const add = () => {
    if (!platform || !handle.trim()) return;
    onChange([...socials, { id: uuid(), platform, handle: handle.trim() }]);
    setPlatform(''); setHandle('');
  };

  return (
    <div className="set-social-fields">
      {socials.map((social, index) => (
        <InputGroup size="sm" className="mb-2" key={social.id}>
          <InputGroup.Text>{options.find((option) => option.key === social.platform)?.label || social.platform}</InputGroup.Text>
          <Form.Control value={social.handle || ''} onChange={(event) => update(social.id, { handle: event.target.value })} />
          <Button variant="outline-secondary" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move up"><BsArrowUp /></Button>
          <Button variant="outline-secondary" onClick={() => move(index, 1)} disabled={index === socials.length - 1} aria-label="Move down"><BsArrowDown /></Button>
          <Button variant="outline-danger" onClick={() => onChange(socials.filter((item) => item.id !== social.id))} aria-label="Remove"><BsTrash /></Button>
        </InputGroup>
      ))}
      {available.length > 0 && (
        <Row className="g-2">
          <Col md={4}><Form.Select size="sm" value={platform} onChange={(event) => setPlatform(event.target.value)}><option value="">Platform...</option>{available.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}</Form.Select></Col>
          <Col><Form.Control size="sm" value={handle} onChange={(event) => setHandle(event.target.value)} placeholder="Handle or URL" /></Col>
          <Col xs="auto"><Button size="sm" onClick={add} disabled={!platform || !handle.trim()}><BsPlus /> Add</Button></Col>
        </Row>
      )}
    </div>
  );
}

export default function FormField({ fieldDef, currentValue, onChange, onChangeField, allValues, setId, onAssetUploaded }) {
  const { key, title, type, placeholder, options = [], min, max, step, unit = '', help } = fieldDef;
  const resolvedOptions = options.length ? options : (fieldDef.presetGroup ? presetOptionsForField(fieldDef.presetGroup) : []);
  const controlId = useMemo(() => `set-field-${key}-${Math.random().toString(36).slice(2)}`, [key]);
  const change = (event) => onChange(type === 'switch' ? event.target.checked : event.target.value);
  let control;

  if (type === 'background-editor') {
    control = <BackgroundField fieldDef={fieldDef} values={allValues} onChangeField={onChangeField} setId={setId} onAssetUploaded={onAssetUploaded} />;
  } else if (type === 'image' || type === 'image-selector') {
    control = <ManagedImageField fieldDef={{ ...fieldDef, options: resolvedOptions }} value={currentValue} onChange={onChange} setId={setId} onAssetUploaded={onAssetUploaded} />;
  } else if (CARD_TYPES.includes(type)) {
    control = <CardField type={type} value={currentValue} onChange={onChange} maxCards={fieldDef.maxCards} />;
  } else if (type === 'richtext') {
    const safeValue = cleanHtml(currentValue || '');
    control = (
      <div
        onPasteCapture={(event) => {
          const clipboard = event.clipboardData;
          const clipboardTypes = Array.from(clipboard?.types || []);
          const hasText = clipboardTypes.includes('text/plain') || clipboardTypes.includes('text/html');
          const isImageOnly = !hasText && Array.from(clipboard?.items || []).some((item) => item.kind === 'file' && item.type.startsWith('image/'));
          if (isImageOnly) event.preventDefault();
        }}
        onDropCapture={(event) => {
          if (Array.from(event.dataTransfer?.files || []).some((file) => file.type.startsWith('image/'))) event.preventDefault();
        }}
      >
        <ReactQuill
          theme="snow"
          value={safeValue}
          onChange={(value) => onChange(cleanHtml(value))}
          modules={QUILL_MODULES}
          formats={QUILL_FORMATS}
          placeholder={placeholder}
        />
      </div>
    );
  } else if (type === 'socials') {
    control = <SocialFields options={resolvedOptions} value={currentValue} onChange={onChange} />;
  } else if (type === 'slider') {
    const value = Number.isFinite(Number(currentValue)) ? Number(currentValue) : Number(fieldDef.defaultValue ?? min ?? 0);
    control = <div className="d-flex gap-3 align-items-center"><Form.Range min={min} max={max} step={step} value={value} onChange={(event) => onChange(Number(event.target.value))} /><output className="set-slider-value">{value}{unit}</output></div>;
  } else if (type === 'textarea') {
    control = <Form.Control as="textarea" rows={4} value={currentValue || ''} onChange={change} placeholder={placeholder} />;
  } else if (type === 'source-tabs') {
    control = <VisualSelector options={resolvedOptions} value={currentValue ?? ''} onChange={onChange} label={title} />;
  } else if (type === 'selector') {
    control = VISUAL_SELECTORS.has(key)
      ? <VisualSelector options={resolvedOptions} value={currentValue ?? ''} onChange={onChange} label={title} />
      : <Form.Select value={currentValue ?? ''} onChange={change}><option value="">Select...</option>{resolvedOptions.map((option) => <option key={option.key} value={option.key}>{option.label}</option>)}</Form.Select>;
  } else if (type === 'switch') {
    control = <Form.Check type="switch" checked={Boolean(currentValue)} onChange={change} label={title} />;
  } else if (type === 'colorpicker') {
    control = <Form.Control type="color" value={currentValue || '#000000'} onChange={change} className="set-color-input" />;
  } else {
    control = <Form.Control type="text" value={currentValue || ''} onChange={change} placeholder={placeholder || title} />;
  }

  return (
    <Form.Group className={`set-form-field set-form-field-${type} set-form-field-${key}`} controlId={controlId}>
      {type !== 'switch' && <Form.Label>{title}</Form.Label>}
      {control}
      {help && <Form.Text className="text-muted d-block">{help}</Form.Text>}
    </Form.Group>
  );
}
